import React from 'react';
import styled from 'styled-components';
import { FaArrowRight } from 'react-icons/fa';

import { ProductArrangementsToUpdate, ProductReplacementPopupInfo } from './types';
import { GetMenuProductArrangementsQuery, MenuProductFragmentFragment } from 'data/__generated__';
import { MenuProductArrangement } from './MenuProductArrangement';
import {
    MEALS_CATEGORY_ID,
    SMALL_BITES_CATEGORY_ID,
    STARTERS_CATEGORY_ID,
    DESSERTS_CATEGORY_ID,
} from 'pages/Products/constants/categories';

const MENU_CATEGORY_LABELS = {
    [MEALS_CATEGORY_ID]: 'Plats',
    [STARTERS_CATEGORY_ID]: 'Entrées',
    [DESSERTS_CATEGORY_ID]: 'Desserts',
    [SMALL_BITES_CATEGORY_ID]: 'Petites faims',
    others: 'Autres',
};

type Rotations =
    GetMenuProductArrangementsQuery['menuProductArrangements'][0]['productArrangementIndexes'][0]['rotations'];
type NextIndex = GetMenuProductArrangementsQuery['menuProductArrangements'][0]['productArrangementIndexes'][0];

type UpdateProductArrangementsToUpdateFn = (arg: {
    productArrangementId: string;
    productArrangementIndex: number;
    rotationNumber: number;
    currentProduct: MenuProductFragmentFragment['product'] | null;
    newProduct: MenuProductFragmentFragment['product'] | null;
    isInProductArrangement: boolean;
}) => void;

function propagateToNextIndex({
    productArrangementId,
    productArrangementIndex,
    rotations,
    nextIndex,
    productArrangementsToUpdate,
    updateProductArrangementsToUpdate,
}: {
    productArrangementId: string;
    productArrangementIndex: number;
    rotations: Rotations;
    nextIndex: NextIndex;
    productArrangementsToUpdate: ProductArrangementsToUpdate;
    updateProductArrangementsToUpdate: UpdateProductArrangementsToUpdateFn;
}) {
    rotations.forEach(({ rotationNumber, products }) => {
        const replacementProduct =
            productArrangementsToUpdate?.[productArrangementId]?.[productArrangementIndex]?.[rotationNumber];

        const productToPropagate =
            replacementProduct === null
                ? null
                : replacementProduct
                  ? replacementProduct.newProduct
                  : products?.[0]?.product || null;

        const nextRotation = nextIndex.rotations.find((r) => r.rotationNumber === rotationNumber);
        const currentProductOfNext = nextRotation?.products?.[0]?.product || null;

        updateProductArrangementsToUpdate({
            productArrangementId,
            productArrangementIndex: nextIndex.productArrangementIndex,
            rotationNumber,
            currentProduct: currentProductOfNext,
            newProduct: productToPropagate,
            isInProductArrangement: true,
        });
    });
}

export const SiteMenus = ({
    menuProductArrangements,
    productArrangementsToUpdate,
    setProductReplacementPopupInfo,
    updateProductArrangementsToUpdate,
}: {
    menuProductArrangements: GetMenuProductArrangementsQuery['menuProductArrangements'];
    productArrangementsToUpdate: ProductArrangementsToUpdate;
    setProductReplacementPopupInfo: (arg: ProductReplacementPopupInfo) => void;
    updateProductArrangementsToUpdate: UpdateProductArrangementsToUpdateFn;
}) => {
    function filterProductArrangements(categoryId: string, targetCategoryId: keyof typeof MENU_CATEGORY_LABELS) {
        if (categoryId === targetCategoryId) return true;
        if (targetCategoryId === 'others') {
            return ![STARTERS_CATEGORY_ID, SMALL_BITES_CATEGORY_ID, MEALS_CATEGORY_ID, DESSERTS_CATEGORY_ID].includes(
                categoryId,
            );
        }
        return false;
    }

    function renderArrangementIndex(
        productArrangementId: string,
        productArrangementName: string,
        productArrangementIndex: number,
        rotations: Rotations,
        nextIndex: NextIndex | undefined,
    ) {
        return (
            <React.Fragment key={`${productArrangementId}-${productArrangementIndex}`}>
                <MenuProductArrangement
                    productArrangementId={productArrangementId}
                    productArrangementName={productArrangementName}
                    productArrangementIndex={productArrangementIndex}
                    rotations={rotations}
                    productArrangementsToUpdate={productArrangementsToUpdate}
                    setProductReplacementPopupInfo={setProductReplacementPopupInfo}
                />
                {nextIndex && (
                    <PropagateArrow
                        onClick={() =>
                            propagateToNextIndex({
                                productArrangementId,
                                productArrangementIndex,
                                rotations,
                                nextIndex,
                                productArrangementsToUpdate,
                                updateProductArrangementsToUpdate,
                            })
                        }
                        title="Propager vers l'index suivant"
                    >
                        <FaArrowRight size={16} />
                    </PropagateArrow>
                )}
            </React.Fragment>
        );
    }

    function renderCategory(categoryId: keyof typeof MENU_CATEGORY_LABELS) {
        const filteredArrangements = [...menuProductArrangements]
            .filter(({ productArrangementMainCategoryId: id }) => filterProductArrangements(id, categoryId))
            .sort((a, b) => a.productArrangementName.localeCompare(b.productArrangementName));

        return (
            <>
                <Title>{MENU_CATEGORY_LABELS[categoryId]}</Title>
                {filteredArrangements.map(
                    ({ productArrangementId, productArrangementName, productArrangementIndexes }) => (
                        <Shelf key={productArrangementId}>
                            <ArrangementColumns>
                                {productArrangementIndexes.map(({ productArrangementIndex, rotations }, arrayIndex) =>
                                    renderArrangementIndex(
                                        productArrangementId,
                                        productArrangementName,
                                        productArrangementIndex,
                                        rotations,
                                        productArrangementIndexes[arrayIndex + 1],
                                    ),
                                )}
                            </ArrangementColumns>
                        </Shelf>
                    ),
                )}
            </>
        );
    }

    return (
        <Column>
            {renderCategory(STARTERS_CATEGORY_ID)}
            {renderCategory(SMALL_BITES_CATEGORY_ID)}
            {renderCategory(MEALS_CATEGORY_ID)}
            {renderCategory(DESSERTS_CATEGORY_ID)}
            {renderCategory('others')}
        </Column>
    );
};

const Column = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 5px;
    padding: 5px;
    width: 100%;
`;

const Shelf = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: auto;
    border: 1px solid ${({ theme }) => theme.darkBorderColor};
    background: ${({ theme }) => theme.backgroundColor};
    margin-bottom: 10px;
`;

const ArrangementColumns = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    align-items: stretch;
`;

const PropagateArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 2px;
    cursor: pointer;
    color: ${({ theme }) => theme.darkBorderColor};
    transition: all 0.2s ease;

    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryHoveredColor};
        transform: scale(1.2);
    }
`;

const Title = styled.span`
    text-align: center;
    font-size: 40px;
    margin-bottom: 10px;
    margin-top: 2px;
`;
